import { combineReducers } from '@reduxjs/toolkit';
import { OrientationTypeEnum } from 'utils/platform';
import { accountReducer } from './account/account.store';
import { configurationReducer } from './configuration/configuration.store';
import { kompisStatesReducer } from './kompisStates/kompisStates.store';
import { navigationReducer } from './navigation/navigation.store';
import { productsReducer } from './products/products.store';
import { productId2ShoppingProduct, shoppingProduct2Product } from './products/products.utils';
import { sceneReducer } from './scene/scene.store';
import { settingsReducer } from './settings/settings.store';
import store from './store';
import { translationsReducer } from './translations/translations.store';
import { userAgentReducer } from './userAgent/userAgent.store';

const rootReducer = combineReducers({
  products: productsReducer,
  navigation: navigationReducer,
  configuration: configurationReducer,
  scene: sceneReducer,
  settings: settingsReducer,
  translations: translationsReducer,
  userAgent: userAgentReducer,
  kompisStates: kompisStatesReducer,
  account: accountReducer
});

export type RootState = ReturnType<typeof rootReducer>;
export default rootReducer;

// create a generic type called AppSelector
type AppSelector<Return> = (state: RootState) => Return;
// create a custom `createSelector` that uses the type above
const createAppSelector = <R>(selector: AppSelector<R>): AppSelector<R> => {
  return selector;
};

// Products states
export const selectProducts = createAppSelector(state => {
  const { products } = state;
  return products.allProducts;
});

export const selectValidSPRs = createAppSelector(state => {
  const { products } = state;
  return products.allValidSPRs;
});

const getShoppingProductsByIds = (ids: string[]) => {
  const states = store.getState();
  return createAppSelector(state => {
    const { products } = state;
    return ids.map(productId2ShoppingProduct(products.allProducts));
  })(states);
};

export const getProductItemsByIds = (ids: string[]) => {
  const states = store.getState();
  const shoppingProducts = getShoppingProductsByIds(ids).filter(Boolean);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return createAppSelector(_state => {
    return shoppingProducts.map(value => {
      return shoppingProduct2Product(value);
    });
  })(states);
};

export const selectVisibleProducts = createAppSelector(state => {
  const { products } = state;
  return products.visibleProducts;
});
export const selectMissingSkyttaProducts = createAppSelector(state => {
  const { products } = state;
  return products.missingSkyttaProducts;
});
export const selectDoorHeightOptionsAvailable = createAppSelector(state => {
  const { products } = state;
  return products.doorHeightOptionsAvailable;
});
export const selectActiveProductId = createAppSelector(state => {
  const { products } = state;
  return products.activeProductId;
});

// Navigation states
export const selectActiveArea = createAppSelector(state => {
  const { navigation } = state;
  return navigation.activeArea;
});
export const selectShowExitPrompt = createAppSelector(state => {
  const { navigation } = state;
  return navigation.showExitPrompt;
});
export const selectSheets = createAppSelector(state => {
  const { navigation } = state;
  return navigation.sheets;
});
export const selectActiveSheetIds = createAppSelector(state => {
  const { navigation } = state;
  return navigation.activeSheets;
});
export const selectActiveSheet = createAppSelector(state => {
  const { navigation } = state;
  return navigation.activeSheets.slice().pop();
});
export const selectSheetAlignment = createAppSelector(state => {
  const { navigation } = state;
  return navigation.sheetAlignment;
});
export const selectVisibleMenuModal = createAppSelector(state => {
  const { navigation } = state;
  return navigation.visibleMenuModal;
});
export const selectLastNavigation = createAppSelector(state => {
  const {
    navigation: { lastNavigation }
  } = state;
  return lastNavigation;
});
export const selectKeyboardArrivedFromSceneIndex = createAppSelector(state => {
  const {
    navigation: { keyboardArrivedFromSceneIndex }
  } = state;
  return keyboardArrivedFromSceneIndex;
});
export const selectNavigateToScene = createAppSelector(state => {
  const {
    navigation: { navigateToScene }
  } = state;
  return navigateToScene;
});

// Configuration states
export const selectSelectedDoor = createAppSelector(state => {
  const { configuration } = state;
  return configuration.selectedDoor;
});
export const selectStorageBehindIsVisible = createAppSelector(state => {
  const { configuration } = state;
  return configuration.storageBehindVisible;
});
export const selectStorageBehind = createAppSelector(state => {
  const { configuration } = state;
  return configuration.storageBehind;
});
export const selectUnsupportedStorageBehind = createAppSelector(state => {
  const { configuration } = state;
  return configuration.unsupportedStorageBehind;
});
export const selectActiveStorageBehindGallery = createAppSelector(state => {
  const { configuration } = state;
  return configuration.activeStorageBehindGallery;
});
export const selectShowErrorPage = createAppSelector(state => {
  const { configuration } = state;
  return configuration.showErrorPage;
});
export const selectShowAppLoadingScreen = createAppSelector(state => {
  const { configuration } = state;
  return configuration.showAppLoadingScreen;
});
export const selectShowLoadingSummaryButton = createAppSelector(state => {
  const { configuration } = state;
  return configuration.showLoadingSummaryButton;
});
export const selectShowSceneLoadingScreen = createAppSelector(state => {
  const { configuration } = state;
  return configuration.showSceneLoadingScreen;
});
export const selectHasUnsavedChanges = createAppSelector(state => {
  const { configuration } = state;
  return configuration.hasUnsavedChanges;
});
export const selectArrivedFromQrCode = createAppSelector(state => {
  const { configuration } = state;
  return configuration.arrivedFromQrCode;
});
export const selectPanelPatternStyles = createAppSelector(state => {
  const { configuration } = state;
  return configuration.selectedPanelPatternStyles;
});
export const selectSelectedPanelStyles = createAppSelector(state => {
  const { configuration } = state;
  return configuration.selectedPanelStyles;
});
export const selectDefaultPanelStyles = createAppSelector(state => {
  const { configuration } = state;
  return configuration.defaultPanelStyles;
});
export const selectReadyToSaveCoreState = createAppSelector(state => {
  const { configuration } = state;
  return configuration.readyToSaveCoreState;
});
export const selectSceneReady = createAppSelector(state => {
  const { configuration } = state;
  return configuration.sceneReady;
});

// kompis states
export const selectStoreId = createAppSelector(state => {
  const { kompisStates } = state;
  return kompisStates.storeId;
});
export const selectZip = createAppSelector(state => {
  const {
    kompisStates: { zip }
  } = state;
  return zip;
});
export const selectStores = createAppSelector(state => {
  const { kompisStates } = state;
  return kompisStates.stores;
});
export const selectShareDesignCardState = createAppSelector(state => {
  const { kompisStates } = state;
  return kompisStates.shareDesignCardState;
});

// Scene states
export const selectIsMeasurementsVisible = createAppSelector(state => {
  const { scene } = state;

  return scene.measurementsVisible;
});
export const selectUserWantsMeasurementVisible = createAppSelector(state => {
  const { scene } = state;

  return scene.userWantsMeasurementVisible;
});
export const selectHasShownIntroAnimation = createAppSelector(state => {
  const { scene } = state;

  return scene.hasShownIntroAnimation;
});
export const selectHasAllSelectedModelsLoaded = createAppSelector(state => {
  const { scene } = state;

  return scene.hasAllSelectedModelsLoaded;
});
export const selectIsDoorsOpened = createAppSelector(state => {
  const { scene } = state;

  return scene.doorsOpened;
});

/**
 * Translations states
 */
export const selectTranslations = createAppSelector(state => {
  const { translations } = state;
  return translations.translations;
});

/**
 * Settings states
 */
export const selectAppSettings = createAppSelector(state => {
  const { settings } = state;
  return settings.appSettings;
});

// UserAgent states
export const selectShouldUseMobileLayout = createAppSelector(state => {
  const { userAgent } = state;
  return userAgent.isMobile;
});

export const selectOrientation = createAppSelector(state => {
  const { userAgent } = state;
  return userAgent.orientation;
});

export const selectIsLandscapeMobile = createAppSelector(state => {
  const {
    userAgent: { orientation, isMobile }
  } = state;
  return isMobile && orientation === OrientationTypeEnum.Landscape;
});

// Account states
export const selectUser = createAppSelector(state => {
  const { account } = state;
  return account.user;
});

export const selectLoggedIn = createAppSelector(state => {
  const { account } = state;
  return account.loggedIn;
});

export const selectAccountVisibleModal = createAppSelector(state => {
  const { account } = state;
  return account.visibleModal;
});

export const selectDesigns = createAppSelector(state => {
  const { account } = state;
  return account.designs;
});

export const selectSaveDesignState = createAppSelector(state => {
  const { account } = state;
  return account.saveDesignState;
});

export const selectCurrentDesignId = createAppSelector(state => {
  const { account } = state;
  return account.currentDesignId;
});

export const selectCurrentDesignName = createAppSelector(state => {
  const { account } = state;
  return account.currentDesignName;
});

// All states
export const selectIsSceneLoadingScreenVisible = createAppSelector(states => {
  const { navigation, scene, configuration } = states;

  return (
    configuration.showSceneLoadingScreen ||
    configuration.showLoadingSummaryButton ||
    !(scene.hasAllSelectedModelsLoaded && navigation.pageLoaded)
  );
});

export const selectIsCustomizeSheetOpen = createAppSelector(states => {
  const {
    navigation: { activeSheets },
    userAgent: { isMobile }
  } = states;

  return (
    activeSheets.some(value => {
      return value.includes('customize') || value.includes('storageBehind');
    }) && isMobile
  );
});
